// Custom iOS Bottom Sheet Component
import React, { useEffect } from "react";
import { createPortal } from "react-dom";

export const IOSBottomSheet: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ isOpen, onClose, children }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  useEffect(() => {
    if (isOpen) {
      // Mount and start enter animation
      setIsVisible(true);
      document.body.style.overflow = "hidden";

      // Trigger slide-up animation after mount
      const timer = setTimeout(() => {
        setIsAnimating(true);
      }, 10); // Small delay to ensure DOM is ready

      return () => clearTimeout(timer);
    } else {
      // Start exit animation
      setIsAnimating(false);

      // Unmount after animation completes
      const timer = setTimeout(() => {
        setIsVisible(false);
        document.body.style.overflow = "unset";
      }, 300); // Match animation duration

      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  if (!isVisible) return null;

  return createPortal(
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop with blur */}
      <div
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300 ease-out ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClose();
        }}
      />

      {/* Bottom sheet */}
      <div
        className="fixed inset-x-0 bottom-0 flex justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-full max-w-none bg-white rounded-t-3xl shadow-2xl"
          style={{
            height: "92vh",
            maxHeight: "95vh",
            transform: isAnimating ? "translateY(0)" : "translateY(100%)",
            opacity: isAnimating ? 1 : 0.9,
            transition:
              "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.3s ease-out",
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};
